import styled from "@emotion/styled";

const mix = (start, end, amount) => Math.round(start + (end - start) * amount)

const hexToRgb = (hex) => {
  const normalized = hex.replace("#", "")
  return {
    r: parseInt(normalized.substring(0, 2), 16),
    g: parseInt(normalized.substring(2, 4), 16),
    b: parseInt(normalized.substring(4, 6), 16),
  }
}

const rgbToHex = ({ r, g, b }) => (
  `#${[r, g, b].map((value) => value.toString(16).padStart(2, "0")).join("")}`
)

const interpolateColor = (from, to, amount) => {
  const start = hexToRgb(from)
  const end = hexToRgb(to)

  return rgbToHex({
    r: mix(start.r, end.r, amount),
    g: mix(start.g, end.g, amount),
    b: mix(start.b, end.b, amount),
  })
}

export const getPublicRecordRateTone = (percent) => {
  if (percent <= 0) {
    return "#1C1C1F"
  }

  if (percent <= 50) {
    return interpolateColor("#9F241F", "#6F6414", (percent - 1) / 49)
  }

  return interpolateColor("#6F6414", "#00940F", (percent - 50) / 50)
}

const semanticToneStyles = (theme, tone) => {
  const tones = {
    discrepancy: {
      color: theme.accent.caution.primaryMedium,
      background: "rgba(204, 123, 0, 0.07)",
      border: "rgba(204, 123, 0, 0.42)",
    },
    renegotiation: {
      color: theme.palette.secondary.dark,
      background: theme.palette.secondary.labelTransparent,
      border: "rgba(145, 103, 156, 0.46)",
    },
    refinance: {
      color: theme.accent.success.secondary,
      background: theme.accent.success.primaryFullestTransparent,
      border: "rgba(0, 148, 15, 0.42)",
    },
    dismissal: {
      color: "#2f6f9f",
      background: "rgba(47, 111, 159, 0.08)",
      border: "rgba(47, 111, 159, 0.38)",
    },
  }

  return tones[tone] || null
}

export const AnalysisPanel = styled.section(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: auto minmax(0, 1fr);
  gap: 1rem;
  overflow: hidden;
`)

export const PanelIntro = styled.div(({ theme }) => `
  width: 100%;
  min-width: 0;
  display: grid;
  gap: 0.25rem;
  padding: 1rem 1.1rem;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.75rem;
  background: ${theme.palette.common.white};
  color: ${theme.palette.common.grayText};
  font-size: 0.92rem;
  font-weight: 550;
  line-height: 1.45;
  text-align: center;
`)

export const TableShell = styled.div(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  display: grid;
  grid-template-rows: minmax(0, 1fr);
  overflow: hidden;
  border: 0.1rem solid ${theme.palette.common.grayBorder};
  border-radius: 0.8rem;
  background: ${theme.palette.common.white};
  box-shadow: 0 0.25rem 0.9rem rgba(15, 23, 42, 0.05);
`)

export const TableViewport = styled.div(({ theme }) => `
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-width: 0;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  overscroll-behavior: contain;

  ::-webkit-scrollbar-thumb {
    border: 0.1rem solid ${theme.palette.common.grayText};
    background-color: ${theme.palette.common.grayText};
  }

  ::-webkit-scrollbar {
    -webkit-appearance: none;
    border: 0.05rem solid ${theme.palette.common.grayBorderDark};
    background-color: ${theme.palette.common.pageBackground};
  }

  ::-webkit-scrollbar:horizontal {
    height: 0.4rem;
  }

  ::-webkit-scrollbar:vertical {
    width: 0.4rem;
  }
`)

export const TableHeaderRow = styled.div(({ theme }) => `
  width: max-content;
  min-width: 100%;
  display: grid;
  align-items: stretch;
  position: sticky;
  top: 0;
  z-index: 5;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.grayBorderLight};
`)

export const HeaderCell = styled.div(({ theme, $center, $tone }) => {
  const tone = semanticToneStyles(theme, $tone)

  return `
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: ${$center ? "center" : "flex-start"};
  gap: 0.35rem;
  padding: 0.75rem 0.8rem;
  border-left: 0.1rem solid ${theme.palette.common.grayBorder};
  border-top: ${tone ? `0.18rem solid ${tone.border}` : "none"};
  color: ${tone ? tone.color : theme.palette.common.grayText};
  background: ${tone ? `linear-gradient(180deg, ${tone.background} 0%, rgba(255, 255, 255, 0) 100%)` : "transparent"};
  font-size: 0.74rem;
  font-weight: 800;
  line-height: 1.22;
  text-transform: uppercase;
  overflow-wrap: anywhere;

  &:first-of-type {
    border-left: none;
  }

  svg {
    width: 1rem;
    height: 1rem;
    color: ${tone ? tone.color : theme.palette.common.grayText};
  }
`
})

export const CategoryRow = styled.div(({ theme }) => `
  width: max-content;
  min-width: 100%;
  display: grid;
  padding: 0.55rem 0.85rem;
  border-top: 0.1rem solid ${theme.palette.common.grayBorder};
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${theme.palette.common.cardBackground};
  color: ${theme.palette.common.black};
  font-size: 0.78rem;
  font-weight: 800;
  line-height: 1.2;
  text-transform: uppercase;
`)

export const CategoryLabel = styled.span`
  grid-column: 1 / -1;
`

export const TableRow = styled.div(({ theme, $even }) => `
  width: max-content;
  min-width: 100%;
  display: grid;
  align-items: center;
  border-bottom: 0.1rem solid ${theme.palette.common.grayBorder};
  background: ${$even ? theme.component.row.even : theme.component.row.odd};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${theme.palette.common.grayBorderLight};
  }
`)

export const DataCell = styled.div(({ theme, $center, $strong, $tone }) => {
  const tone = semanticToneStyles(theme, $tone)

  return `
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: ${$center ? "center" : "flex-start"};
  padding: 0.52rem 0.8rem;
  color: ${tone ? tone.color : theme.palette.common.black};
  background: ${tone ? tone.background : "transparent"};
  font-size: 0.86rem;
  font-weight: ${$strong ? "750" : "500"};
  line-height: 1.28;
  overflow-wrap: anywhere;

  & + & {
    border-left: 0.1rem solid ${theme.palette.common.grayBorder};
  }
`
})

export const FieldPill = styled.span(({ theme, $tier }) => {
  const colorMap = {
    one: {
      border: theme.palette.common.redDark,
      background: "rgba(191, 48, 41, 0.12)",
      color: theme.palette.common.redDark,
    },
    two: {
      border: theme.palette.secondary.main,
      background: theme.palette.secondary.labelTransparent,
      color: theme.palette.secondary.dark,
    },
    three: {
      border: theme.accent.success.primary,
      background: theme.accent.success.primaryFullestTransparent,
      color: theme.accent.success.secondary,
    },
  }
  const colors = colorMap[$tier] || colorMap.three

  return `
    width: fit-content;
    max-width: 100%;
    display: inline-flex;
    align-items: center;
    min-height: 1.55rem;
    padding: 0.18rem 0.62rem;
    border: 0.1rem solid ${colors.border};
    border-radius: 999rem;
    background: ${colors.background};
    color: ${colors.color};
    font-size: 0.74rem;
    font-weight: 800;
    line-height: 1.1;
    text-transform: uppercase;
    overflow-wrap: anywhere;
  `
})

export const StatusIconCell = styled(DataCell)(({ theme }) => `
  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`)

export const PercentValue = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.42rem;
  min-width: 0;
`

export const PercentText = styled.span(({ $tone }) => `
  color: ${$tone};
`)

export const PercentRing = styled.span(({ $percent, $tone }) => `
  width: 1.45rem;
  height: 1.45rem;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999rem;
  background:
    radial-gradient(circle at center, #ffffff 0 55%, transparent 56%),
    conic-gradient(${$tone} ${Math.max(0, Math.min(100, $percent)) * 3.6}deg, rgba(148, 163, 184, 0.24) 0deg);
  box-shadow: inset 0 0 0 0.08rem rgba(148, 163, 184, 0.28);
`)

export const PercentCheck = styled.span(({ $tone }) => `
  color: ${$tone};
  font-size: 0.75rem;
  font-weight: 900;
  line-height: 1;
`)
